import React from 'react';

const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'No consta';
    return num.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const TimelineNode = ({ entry, isLast }) => {
    const { carga, statusLabel, textColor, iconClass, razon, afeccionExtra } = entry;
    
    // Set colors based on status
    let bubbleColor = "bg-gray-100 border-gray-300 text-gray-500";
    if (textColor.includes("red")) {
        bubbleColor = "bg-red-100 border-red-300 text-red-600";
    } else if (textColor.includes("green")) {
        bubbleColor = "bg-green-100 border-green-300 text-green-600";
    } else if (textColor.includes("sky")) {
        bubbleColor = "bg-sky-100 border-sky-300 text-sky-600";
    } else if (textColor.includes("amber")) {
        bubbleColor = "bg-amber-100 border-amber-300 text-amber-600";
    }

    return (
        <div className="relative pl-8 sm:pl-10 py-4 group">
            {/* Timeline vertical line */}
            {!isLast && (
                <div className="absolute left-[15px] sm:left-[19px] top-10 bottom-[-16px] w-[2px] bg-gray-200 group-last:hidden"></div>
            )}
            
            {/* Timeline dot/bubble */}
            <div className={`absolute left-0 sm:left-1 top-5 w-8 h-8 rounded-full border-2 flex items-center justify-center bg-white shadow-sm z-10 ${bubbleColor}`}>
                <i className={`${iconClass} text-sm`}></i>
            </div>

            {/* Content card */}
            <div className={`bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow relative
                ${textColor.includes('red') ? 'border-red-200 hover:border-red-300' : ''}
                ${textColor.includes('green') ? 'border-green-200 hover:border-green-300' : ''}
                ${textColor.includes('amber') ? 'border-amber-200 hover:border-amber-300' : ''}
                ${textColor.includes('sky') ? 'border-sky-200 hover:border-sky-300' : ''}
            `}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        {/* Tags area */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${bubbleColor.replace('border-', '')} border`}>
                                {statusLabel}
                            </span>
                            {carga.tipo_relacion && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-gray-100 text-gray-600 border border-gray-200">
                                    <i className="fas fa-link mr-1"></i>
                                    {carga.tipo_relacion}
                                </span>
                            )}
                        </div>
                        
                        <h4 className="text-base font-semibold text-gray-900 leading-tight mb-1">{carga.nombre}</h4>
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                            <span><i className="far fa-calendar-alt mr-1"></i> {carga.fecha || 'Sin fecha'}</span>
                            {carga.num_inscripcion && (
                                <span><i className="far fa-file-alt mr-1"></i> Inscripción {carga.num_inscripcion}</span>
                            )}
                        </div>

                        {carga.descripcion && (
                            <p className="text-sm text-gray-600 mb-2 leading-relaxed">{carga.descripcion}</p>
                        )}

                        {razon && (
                            <div className={`text-sm mt-3 ${textColor} bg-opacity-10 p-3 rounded-md bg-gray-50 flex items-start`}>
                                <i className="fas fa-info-circle mt-0.5 mr-2"></i>
                                <span>
                                    {razon}
                                    {afeccionExtra || ''}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="sm:text-right flex-shrink-0 pt-1 sm:pt-0">
                        <p className="text-lg font-bold text-gray-900 border-t sm:border-0 border-gray-100 pt-2 sm:pt-0 mt-2 sm:mt-0">
                            {carga.valor && carga.valor !== 'No consta' && parseFloat(carga.valor) > 0
                                ? formatCurrency(carga.valor)
                                : <span className="text-sm text-gray-400 font-normal">Sin valor o No consta</span>
                            }
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CargasTimeline = ({ noCanceladas, canceladas, requierenRevision, gruposCargas = [] }) => {
    // Combine all entries
    const allEntries = [...noCanceladas, ...canceladas, ...requierenRevision];
    
    if (allEntries.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                <i className="fas fa-check-circle text-4xl text-green-300 mb-3 block"></i>
                <p>No se han encontrado cargas para este bien.</p>
            </div>
        );
    }

    // Sort by chronological order using the id (since the prompt extracts them chronologically)
    // Or try to parse dates if possible. For simplicity, we use the original order provided by the AI 
    // which is chronological (the prompt asks to present chronologically).
    // We'll rely on generating a unified list in the order the AI provided, assuming it's roughly chronological.
    // Let's sort them back to their original AI output order if we had that, but since we only have the classified ones,
    // let's try to extract numbers from 'num_inscripcion' or 'id' for sorting if needed.
    // Actually, AI usually generates them chronologically based on ID or index. 
    // To be safe, let's sort by ID assuming ids are like "carga_1", "carga_2"
    allEntries.sort((a, b) => {
        const idA = parseInt(String(a.carga.id || '').replace(/\D/g, '') || 0);
        const idB = parseInt(String(b.carga.id || '').replace(/\D/g, '') || 0);
        return idA - idB;
    });

    // Grouping logic
    const groups = {};
    const unassigned = [];

    allEntries.forEach(entry => {
        const groupKey = entry.carga.grupo;
        if (groupKey) {
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(entry);
        } else {
            unassigned.push(entry);
        }
    });

    // Each group is a block in the timeline
    return (
        <div className="space-y-8 bg-gray-50 p-2 sm:p-6 rounded-xl border border-gray-200">
            {Object.keys(groups).map((groupKey, groupIndex) => {
                const groupEntries = groups[groupKey];
                const groupSummaryData = gruposCargas.find(g => g.id_grupo === groupKey);
                
                // Calculate the true deduplicated total for the group (the max value among its entries, assuming duplicates)
                const groupDeduplicatedTotal = Math.max(...groupEntries.map(e => parseFloat(e.carga.valor) || 0), 0);

                return (
                    <div key={groupKey} className="relative">
                        {/* Group label and summary */}
                        <div className="sticky top-0 z-20 mb-3 bg-gray-50 pb-2">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 bg-white p-4 border border-blue-200 rounded-xl shadow-sm">
                                <div className="flex-1">
                                    <span className="inline-flex items-center text-blue-800 text-sm font-bold bg-blue-100 px-3 py-1 rounded mb-2">
                                        <i className="fas fa-layer-group mr-2 opacity-70"></i>
                                        Grupo de Cargas: {groupKey.replace(/_/g, ' ').toUpperCase()}
                                    </span>
                                    {groupSummaryData && groupSummaryData.resumen && (
                                        <p className="text-gray-700 text-sm leading-relaxed mt-1 border-l-2 border-blue-300 pl-3">
                                            {groupSummaryData.resumen}
                                        </p>
                                    )}
                                </div>
                                <div className="sm:text-right flex-shrink-0 bg-gray-50 p-2 rounded-lg border border-gray-100 min-w-[140px] flex flex-col justify-center">
                                    <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">Carga real del grupo</span>
                                    <span className="text-lg font-bold text-gray-900 border-t border-gray-200 pt-1">
                                        {formatCurrency(groupDeduplicatedTotal)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white border border-gray-200 rounded-xl p-2 sm:p-4 shadow-sm relative overflow-hidden">
                            {/* Subtle background connecting line for the whole group */}
                            <div className="absolute left-[31px] sm:left-[35px] top-6 bottom-6 w-[2px] bg-blue-100 z-0 hidden sm:block"></div>
                            
                            {groupEntries.map((entry, idx) => (
                                <TimelineNode 
                                    key={entry.carga.id || idx} 
                                    entry={entry} 
                                    isLast={idx === groupEntries.length - 1} 
                                />
                            ))}
                        </div>
                    </div>
                );
            })}

            {unassigned.length > 0 && (
                <div className="relative">
                    {Object.keys(groups).length > 0 && (
                        <div className="sticky top-0 z-20 mb-2 mt-8">
                            <span className="inline-flex items-center bg-gray-500 text-white text-xs font-bold px-3 py-1.5 rounded shadow-sm">
                                <i className="fas fa-layer-group mr-2"></i>  Otras Cargas Individuales
                            </span>
                        </div>
                    )}
                    <div className="bg-white border border-gray-200 rounded-xl p-2 sm:p-4 shadow-sm">
                        {unassigned.map((entry, idx) => (
                            <TimelineNode 
                                key={entry.carga.id || idx} 
                                entry={entry} 
                                isLast={idx === unassigned.length - 1} 
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CargasTimeline;
